import {Component, computed, effect, inject, signal} from '@angular/core';
import {CreateTaTimelineComponent} from "../../components/create-ta/create-ta-timeline/create-ta-timeline.component";
import {TaAppStore} from "../../../state/clcta-store";
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Textarea} from 'primeng/textarea';
import {CreateTaStepType} from '../../../util/app.types';
import {Chip} from 'primeng/chip';
import {ActivatedRoute, Router} from '@angular/router';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {TaCostBubbleComponent} from '../../components/ta-cost-bubble/ta-cost-bubble.component';
import {ViewTaComponent} from '../../components/view-ta/view-ta.component';
import {TaAuditLogButtonComponent} from "../../components/ta-audit-log-button/ta-audit-log-button.component";
import {ConfirmationWrapperService} from '../../../services/confirmation-wrapper.service';
import {PermissionService} from '../../../services/permission.service';
import {Tooltip} from 'primeng/tooltip';
import {TaToastComponent} from '../../components/ta-toast/ta-toast.component';
import {TAStatus} from '../../../api';
import {DatePipe} from '@angular/common';
import {ErrorMessageService} from '../../../services/error-message.service';
import {ReturnNavigationService} from '../../../services/return-navigation.service';
import {latestItinerariesForSubmission, requiresDocumentation} from '../../../util/itinerary.utils';

@Component({
  selector: 'clc-review-ta-page',
  imports: [
    CreateTaTimelineComponent,
    Button,
    Card,
    ReactiveFormsModule,
    Textarea,
    FormsModule,
    Chip,
    TaCostBubbleComponent,
    ViewTaComponent,
    TaAuditLogButtonComponent,
    Tooltip,
    TaToastComponent,
    DatePipe,
  ],
  templateUrl: './clc-review-ta-page.html',
  styleUrl: './clc-review-ta-page.scss'
})
export class ClcReviewTaPage {
  store = inject(TaAppStore);
  route = inject(ActivatedRoute);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  permissionService = inject(PermissionService);
  confirmationServiceWrapper = inject(ConfirmationWrapperService);
  router = inject(Router);
  errorMessageService = inject(ErrorMessageService);
  returnNavigation = inject(ReturnNavigationService);

  taStatus = computed<TAStatus | undefined>(() => this.store.taSubmission()?.status);
  isFlightOnly = this.taSubmissionDatasource.isFlightOnly;
  taNotes: string = "";
  estimatedTaValue = computed<number>(() => {
    let sum = 0;
    let amounts = this.store.taValue()?.travellerAmounts;
    if (!!amounts) {
      for (const key in amounts) {
        sum += amounts[key];
      }
    }
    return sum;
  });
  isPrePaid = computed(() => !!this.store.taSubmission()?.isPrePaid);
  // Total less day trips; falls back to the total until the server reports it.
  private currentFinanceValue = computed(() => this.store.taValue()?.currentFinanceValue ?? this.estimatedTaValue());
  // Processed pre-payment, else what processing would snapshot.
  prepaymentComponent = computed(() => this.store.taValue()?.prePaidValue ?? this.currentFinanceValue());
  dayTripComponent = computed(() => this.estimatedTaValue() - this.currentFinanceValue());

  /**
   * Mirrors the server's definition of a day trip: ItineraryUtil sets
   * `TripStructure.IsDayTrip = items.Count == 1`, and that is what drives WorkType "Day" and
   * CalculateDayTripCost, which legitimately prices a sub-10-hour day trip at $0. The TA's own
   * start/end dates are the wrong test - the date window only caps how many itinerary rows the
   * user may add, so a single-row day trip inside a three-day window would read as multi-day and
   * warn about a $0 that is correct. Keep this in step with ItineraryUtil if that rule changes.
   *
   * An itinerary that is absent or empty is not treated as multi-day: it is also the state while
   * the read-only itinerary step inside view-ta is still fetching, and a warning must not flash
   * during load.
   */
  isMultiDayTrip = computed<boolean>(() => {
    const itinerary = this.store.currentItinerary();
    if (!itinerary || !this.belongsToCurrentTa(itinerary.submissionId)) {
      return false;
    }
    return (itinerary.items?.length ?? 0) > 1;
  });

  showZeroValueWarning = computed<boolean>(() => {
    if (this.isFlightOnly()) {
      return false;
    }
    const estimate = this.store.taValue();
    if (!estimate || !this.belongsToCurrentTa(estimate.submissionId)) {
      return false;
    }
    return this.isMultiDayTrip() && this.estimatedTaValue() === 0;
  });

  // Advisory only: CTM booking documents are expected but not required to submit.
  showNoFlightDocumentsWarning = computed<boolean>(() =>
    this.isFlightOnly() && this.store.taDocuments().length === 0);

  // Advisory only (CLC-199). Flight-only TAs have their own warning above.
  showMissingDocumentsWarning = computed<boolean>(() =>
    !this.isFlightOnly() && this.store.taDocuments().length === 0
    && latestItinerariesForSubmission(this.taSubmissionDatasource.allItineraries(), this.store.taSubmission()?.id)
      .some(itinerary => requiresDocumentation(itinerary.items ?? [])));

  /**
   * Same staleness guard the store applies to incoming estimates: only a definite mismatch
   * disqualifies the data, so a payload without a submissionId is still trusted.
   */
  private belongsToCurrentTa(submissionId: string | null | undefined): boolean {
    const currentId = this.store.taSubmission()?.id;
    return !submissionId || !currentId || submissionId.toLowerCase() === currentId.toLowerCase();
  }

  submitting = signal(false);
  canSubmit = computed(() => this.permissionService.canSubmit() && this.taSubmissionDatasource.isReviewValid().isValid
    && !this.taSubmissionDatasource.hasOnlyCasualTravellers());
  cantSubmitMessage = computed(() => {
    if (this.canSubmit()) {
      return '';
    }
    //return !this.taSubmissionDatasource.isReviewValid().isValid ? this.taSubmissionDatasource.isReviewValid().message : undefined
    return "Check all required fields are complete and itinerary is filled out";
  });

  withholdApproval = computed(() => {
    const approvals = this.store.taSubmission()?.approvals ?? [];
    console.log(approvals);
    const withheld = approvals.filter(a => a.actionType === 'Withhold' && a.status === 'Actioned');
    return withheld.length ? withheld[withheld.length - 1] : null;
  });
  withheldData = computed(() => {
    const approval = this.withholdApproval();
    if (!!approval) {
      return {
        approverName: approval.actionEvent?.createdByName,
        approverEmail: approval.actionEvent?.createdByEmail,
        approverNotes: '', //approval.notes,
        date: approval.actionedDate ? new Date(approval.actionedDate) : null,
      }
    }
    return null;
  })
  hasApprovalHistory = computed(() => (this.store.taSubmission()?.approvals?.length ?? 0) > 0);
  canCancel = computed(() => this.permissionService.canCancel());
  private initialEstimateRan = false;

  constructor() {
    effect(() => {
      let id = this.store.taSubmission()?.id;
      if (!!id && !this.initialEstimateRan){
        this.initialEstimateRan = true;
        this.taSubmissionDatasource.updateTaEstimateValue(id);
        this.taSubmissionDatasource.getAllItineraries();
      }
    });
    // If the user manual enters the review page when the TA is not in progress
    // they get auto routed to the manage version of the same page. Same for create-ta-page.
    effect(() => {
      const status = this.taStatus();
      const id = this.store.taSubmission()?.id;
      if (status !== 'InProgress' && id) {
        this.router.navigate([`/ta/${id}/manage`]);
      }
    });
    // Re-run once the TA loads: a flight-only TA has its own prefix and icon.
    effect(() => {
      const flightOnly = this.isFlightOnly();
      this.store.updateBreadCrumbData({
        title: 'Review TA',
        taNumberPrefix: flightOnly ? 'Flights Only ' : 'Review ',
        titleIcon: flightOnly ? 'fa-solid fa-plane fa-lg' : 'fa-regular fa-location-dot fa-lg',
        showTaComments: true,
        showSaveStatus: true
      });
    });
  }

  submitTaFinal(): void {
    if (this.submitting()) {
      return;
    }
    this.submitting.set(true);
    this.taSubmissionDatasource.submitTAForApproval(this.store.taSubmission()?.id ?? undefined, this.taNotes).subscribe({
      next: response => {
        if (response.success && response.data) {
          // Resolved before resetTaState, which clears the TA the delegate tab is derived from.
          const returnUrl = this.returnNavigation.resolveReturnUrl('/home');
          // No submitting reset — we navigate away.
          this.store.resetTaState();
          this.taSubmissionDatasource.resetSource();
          this.router.navigateByUrl(returnUrl);
        } else {
          this.submitting.set(false);
          console.error('Submission failed: ', response);
          this.errorMessageService.showTemporaryErrorWithTitle('Submission Failed', `${response.error ?? ''}`, 10000);
        }
      },
      error: error => {
        this.submitting.set(false);
        console.error('Server error: ', error);
        this.errorMessageService.showTemporaryError('There was an error submitting your TA', 5000);
      }
    });
  }

  updateEstimate(): void {
    this.taSubmissionDatasource.updateTaEstimateValue(this.store.taSubmission()!.id!);
    this.taSubmissionDatasource.updateTaPrepaidStatus(this.store.taSubmission()!.id!);
    // An edit dialog may have changed travel modes or accommodation.
    this.taSubmissionDatasource.getAllItineraries();
  }

  deleteSubmissionClicked(): void {
    // Resolved up front for the same reason as submitTaFinal — deleting clears the current TA.
    const returnUrl = this.returnNavigation.resolveReturnUrl('/home');
    this.confirmationServiceWrapper.deleteConfirm().subscribe({
      next: () => {
        this.router.navigateByUrl(returnUrl);
      },
      error: error => {
        console.error('Error deleting: ', error);
        this.errorMessageService.showTemporaryError('There was an error deleting your TA', 5000);
      }
    });
  }

  cancelSubmissionClicked(): void {
    // Resolved up front, matching deleteSubmissionClicked.
    const returnUrl = this.returnNavigation.resolveReturnUrl('/home');
    this.confirmationServiceWrapper.cancelConfirm(this.store.taSubmission()).subscribe({
      next: () => {
        this.router.navigateByUrl(returnUrl);
      },
      error: error => {
        console.error('Error cancelling: ', error);
        this.errorMessageService.showTemporaryError('There was an error cancelling your TA', 5000);
      }
    });
  }

  protected readonly CreateTaStepType = CreateTaStepType;
}
